import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, X, Upload, Wand2, Image as ImageIcon, LibraryBig, ArrowLeft, Film, Link2, Check } from 'lucide-react';
import { MediaLibraryList } from './MediaLibraryList';

const API = '/api'; // Same-origin: /api routes on the serving domain (Preview or Production custom domain) to backend via Emergent ingress. Avoids cross-domain CORS / tracking-prevention failures.

/**
 * Image picker used inside Newsletter & Email studios.
 * Sources: AI generate + file upload + Cloudinary Library. Returns a hosted
 * (Cloudinary) image URL via onImageSet.
 *
 * Props:
 *   testIdPrefix: data-testid prefix to keep tests targeting unique slots
 *   imageData: current image data URI (or null)
 *   onImageSet: callback (imageData|null) => void
 *   context: 'hero' | 'newsletter' | 'email' | 'announcement' — affects AI prompt
 *   promptContext: string passed to AI ("section title / email subject" etc.)
 *   label: header label shown above the picker (e.g. "Hero Image", "Section Image")
 *   size: 'compact' | 'default' — controls preview height
 *   brandCorner: when true, uploaded/library images get the HG logo-in-corner
 *     overlay applied (white logo, subtle, bottom-right, NO text) before being
 *     stored. Used by the Weekly Fitter header. AI images are left as-is — they
 *     already carry the baked watermark, so re-branding would double the logo.
 *   libraryFolder: Cloudinary folder for the Library tab (default 'hg-library')
 */
export function EmailImagePanel({
  testIdPrefix = 'email-img',
  imageData,
  onImageSet,
  context = 'email',
  promptContext = '',
  label = 'Image',
  size = 'default',
  brandCorner = false,
  brandToggle = false,
  libraryFolder = 'hg-library',
}) {
  const [generating, setGenerating] = useState(false);
  const [processing, setProcessing] = useState(false);
  // Opt-in HG watermark (when brandToggle is on). Default OFF; checked BEFORE
  // uploading/selecting brands that image via the same logo-corner path.
  const [branded, setBranded] = useState(false);
  const [error, setError] = useState('');
  const [aiPrompt, setAiPrompt] = useState(''); // optional user instructions for AI generation
  const [view, setView] = useState('idle'); // 'idle' | 'library'
  // Video the asset library holds: a still frame to put IN the email, and the hosted film
  // URL to link to. They arrive from the same endpoint under their own key - they are not in
  // `libraryFolder`, and mixing them into `images` would quietly change what that folder
  // means. `unusable` carries the films we hold but cannot link, with the reason.
  const [library, setLibrary] = useState({ loading: false, images: [], videos: [], unusable: [], media: [], mediaError: '', loaded: false });
  const [copiedWatch, setCopiedWatch] = useState('');

  // Apply the HG logo-in-corner overlay when brandCorner is on; otherwise pass
  // the hosted URL straight through. Returns the URL to store.
  const applyBranding = useCallback(async (url) => {
    // Brand when always-on (brandCorner) OR the opt-in toggle is checked.
    if ((!brandCorner && !branded) || !url) return url;
    try {
      const res = await axios.post(`${API}/email/brand-image`, {
        source: url,
        treatment: 'logo-corner',
      });
      return res.data.image_url || url;
    } catch {
      // Branding is additive — if it fails, fall back to the unbranded hosted URL.
      return url;
    }
  }, [brandCorner, branded]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await axios.post(`${API}/email/generate-image`, {
        // Typed instructions win; blank = let the software decide (current behavior).
        prompt: aiPrompt.trim() || promptContext || 'premium golf instruction and custom fitting',
        context,
      });
      // AI images already carry the baked watermark — do NOT re-brand.
      onImageSet(res.data.image_url);
    } catch (e) {
      setError(e.response?.data?.detail || 'Image generation failed');
    }
    setGenerating(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setProcessing(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/email/upload-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const finalUrl = await applyBranding(res.data.image_url);
      onImageSet(finalUrl);
    } catch {
      setError('Upload failed');
    }
    setProcessing(false);
  };

  const loadLibrary = useCallback(async () => {
    setLibrary((s) => ({ ...s, loading: true }));
    setError('');
    try {
      const res = await axios.get(`${API}/email/library-images`, { params: { folder: libraryFolder } });
      setLibrary({
        loading: false,
        images: res.data.images || [],
        videos: res.data.video_posters || [],
        unusable: res.data.video_posters_unusable || [],
        media: res.data.media_library?.assets || [],
        // "the library is down" and "the library has no images for email" are different
        // facts, and a composer needs to know which one they are looking at.
        mediaError: res.data.media_library?.error || '',
        loaded: true,
      });
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load library');
      setLibrary({ loading: false, images: [], videos: [], unusable: [], media: [], mediaError: '', loaded: true });
    }
  }, [libraryFolder]);

  useEffect(() => {
    if (view === 'library' && !library.loaded && !library.loading) {
      loadLibrary();
    }
  }, [view, library.loaded, library.loading, loadLibrary]);

  const handleLibrarySelect = async (img) => {
    setError('');
    setProcessing(true);
    try {
      const finalUrl = await applyBranding(img.secure_url);
      onImageSet(finalUrl);
      setView('idle');
    } catch {
      setError('Could not select image');
    }
    setProcessing(false);
  };

  const previewHeight = size === 'compact' ? 'h-28' : 'h-40';

  // --- Library grid view ---
  if (view === 'library') {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">{label} · Library</span>
          <button
            data-testid={`${testIdPrefix}-library-back`}
            onClick={() => setView('idle')}
            className="text-[10px] text-hg-text-dim hover:text-hg-green font-sans flex items-center gap-1"
          >
            <ArrowLeft size={10} /> Back
          </button>
        </div>
        {/* --- Film the Hub holds: a poster to drop in, and a link to watch it ---------
            The poster is an ordinary image once it is chosen, so it goes through exactly the
            same branding and selection path as anything else here. The watch link is NOT
            inserted anywhere: it is offered to be copied, because where a link belongs in a
            newsletter is an editorial decision and this panel does not get to make it. */}
        {library.videos.length > 0 && (
          <div className="border border-hg-border rounded-sm p-2 bg-hg-bg/40 space-y-2" data-testid={`${testIdPrefix}-video-posters`}>
            <div className="flex items-center gap-1.5 text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">
              <Film size={11} /> From film — poster still + watch link
            </div>
            {library.videos.map((v) => (
              <div key={v.public_id} className="flex items-center gap-2">
                <button
                  data-testid={`${testIdPrefix}-video-poster-item`}
                  onClick={() => handleLibrarySelect(v)}
                  disabled={processing}
                  title={v.filename || v.public_id}
                  className="w-16 h-10 shrink-0 rounded-sm overflow-hidden border border-hg-border hover:border-hg-green transition-colors disabled:opacity-40"
                >
                  <img src={v.thumbnail_url} alt={v.filename || v.public_id} className="w-full h-full object-cover" loading="lazy" />
                </button>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-sans text-hg-text truncate">{v.filename || v.public_id}</div>
                  <div className="text-[10px] font-sans text-hg-text-dim">
                    {v.aspect || '—'}{v.duration_s ? ` · ${Math.round(v.duration_s)}s` : ''} · click the still to use it
                  </div>
                </div>
                <button
                  data-testid={`${testIdPrefix}-video-watch-copy`}
                  onClick={() => { navigator.clipboard?.writeText(v.video_url); setCopiedWatch(v.public_id); }}
                  className="shrink-0 inline-flex items-center gap-1 text-[10px] font-sans text-hg-text-dim hover:text-hg-green border border-hg-border rounded-sm px-1.5 py-1"
                  title={v.video_url}
                >
                  {copiedWatch === v.public_id ? <Check size={10} /> : <Link2 size={10} />}
                  {copiedWatch === v.public_id ? 'Copied' : 'Watch link'}
                </button>
              </div>
            ))}
          </div>
        )}
        {/* --- The HG media library -----------------------------------------------------
            The Hub owns the library and every project reads the feed; this panel is one of
            those readers. Audio renders as a play control and a download and NEVER a poster:
            Cloudinary files audio under resource_type "video", so anything keying off that
            would ask for a still frame of a sound file and show a broken thumbnail. `kind`
            is what the asset is; resource_type is only how Cloudinary files it. */}
        {library.mediaError && (
          <div className="text-[10px] font-sans text-amber-300" data-testid={`${testIdPrefix}-media-error`}>
            {library.mediaError} — this is the library being unreadable, not the library being empty.
          </div>
        )}
        {/* The same list Social Studio's "Insert from media library" renders — one component,
            so the two readers of the feed cannot drift. Here a pick drops the film's POSTER (or
            the still itself) into the slot; audio has no still, so it offers play + download. */}
        <MediaLibraryList
          items={library.media}
          testIdPrefix={testIdPrefix}
          busy={processing}
          showDownload
          onPick={(m) => handleLibrarySelect({ secure_url: m.poster_url || m.url, public_id: m.id })}
          note={(m) => (m.kind === 'audio' ? ' · play or download — no still to insert' : '')}
        />
        {/* A film we hold and cannot link is NOT silently absent from the list above - an
            empty strip reads as "there is no film", which sends whoever is composing to look
            in the wrong place. */}
        {library.unusable.length > 0 && (
          <div className="text-[10px] font-sans text-amber-300" data-testid={`${testIdPrefix}-video-unusable`}>
            {library.unusable.map((u, i) => (
              <div key={i}>{u.filename || 'a film'}: {u.reason}</div>
            ))}
          </div>
        )}

        <div className="border border-hg-border rounded-sm p-2 bg-hg-bg/40 min-h-[7rem]">
          {library.loading ? (
            <div className="flex items-center justify-center h-24 text-hg-text-dim text-[11px] font-sans gap-2">
              <Loader2 size={13} className="animate-spin" /> Loading library…
            </div>
          ) : library.images.length === 0 ? (
            <div className="flex items-center justify-center h-24 text-center text-hg-text-dim text-[11px] font-sans px-3">
              No images yet — upload some to your Cloudinary <span className="font-mono mx-1">{libraryFolder}</span> folder.
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2 max-h-56 overflow-y-auto">
              {library.images.map((img) => (
                <button
                  key={img.public_id}
                  data-testid={`${testIdPrefix}-library-item`}
                  onClick={() => handleLibrarySelect(img)}
                  disabled={processing}
                  title={img.public_id}
                  className="relative aspect-square rounded-sm overflow-hidden border border-hg-border hover:border-hg-green transition-colors disabled:opacity-40"
                >
                  <img src={img.thumbnail_url} alt={img.public_id} className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>
        {processing && <p className="text-[9px] text-hg-text-dim font-sans">Applying branding…</p>}
        {error && <p className="text-[10px] text-red-400 font-sans">{error}</p>}
      </div>
    );
  }

  // --- Default idle / preview view ---
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">{label}</span>
        {imageData && (
          <button
            data-testid={`${testIdPrefix}-remove`}
            onClick={() => onImageSet(null)}
            className="text-[10px] text-hg-text-dim hover:text-hg-green font-sans flex items-center gap-1"
          >
            <X size={10} /> Remove
          </button>
        )}
      </div>
      {imageData ? (
        <div className="relative group">
          <img
            data-testid={`${testIdPrefix}-preview`}
            src={imageData}
            alt={label}
            className={`w-full ${previewHeight} object-cover rounded-sm border border-hg-border`}
          />
        </div>
      ) : (
        <div className="border border-dashed border-hg-border rounded-sm p-3 flex flex-col items-center gap-2 bg-hg-bg/40">
          <ImageIcon size={16} className="text-hg-text-dim" strokeWidth={1.5} />
          <input
            type="text"
            data-testid={`${testIdPrefix}-ai-prompt`}
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            placeholder="Describe the AI image (optional — blank lets the software decide)"
            className="w-full bg-hg-bg border border-hg-border rounded-sm px-2 py-1 text-[11px] font-sans text-hg-text placeholder:text-hg-text-dim focus:border-hg-green focus:outline-none"
          />
          <div className="flex flex-wrap justify-center gap-2">
            <button
              data-testid={`${testIdPrefix}-generate`}
              onClick={handleGenerate}
              disabled={generating || processing}
              className="flex items-center gap-1.5 px-3 py-1 text-[11px] font-sans bg-hg-green text-hg-text rounded-sm hover:bg-hg-green-hover transition-colors disabled:opacity-40"
            >
              {generating ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
              {generating ? 'Generating…' : 'AI Generate'}
            </button>
            <label
              data-testid={`${testIdPrefix}-upload`}
              className="flex items-center gap-1.5 px-3 py-1 text-[11px] font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green hover:text-hg-green transition-colors cursor-pointer"
            >
              {processing ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />} Upload
              <input type="file" accept="image/*" onChange={handleUpload} className="hidden" disabled={processing} />
            </label>
            <button
              data-testid={`${testIdPrefix}-library`}
              onClick={() => setView('library')}
              disabled={generating || processing}
              className="flex items-center gap-1.5 px-3 py-1 text-[11px] font-sans border border-hg-border text-hg-text-dim rounded-sm hover:border-hg-green hover:text-hg-green transition-colors disabled:opacity-40"
            >
              <LibraryBig size={11} /> Library
            </button>
          </div>
          {brandToggle && (
            <label
              data-testid={`${testIdPrefix}-watermark-toggle`}
              className="flex items-center gap-1.5 text-[10px] font-sans text-hg-text-dim cursor-pointer mt-0.5"
              title="Adds the HG logo to the bottom-right (same as Weekly Fitter). Check before uploading."
            >
              <input type="checkbox" checked={branded} onChange={(e) => setBranded(e.target.checked)} className="accent-hg-green" />
              Add HG watermark <span className="text-hg-text-dim/70">(check before upload — leave off for logos/video stills)</span>
            </label>
          )}
          {generating && <p className="text-[9px] text-hg-text-dim font-sans">~20-30 seconds</p>}
          {processing && !generating && <p className="text-[9px] text-hg-text-dim font-sans">Applying branding…</p>}
        </div>
      )}
      {error && <p className="text-[10px] text-red-400 font-sans">{error}</p>}
    </div>
  );
}

export default EmailImagePanel;
